import { useMemo, useState } from "react";
import { CrmLead, logEvent, updateLead } from "@/lib/crm";
import {
  MessageTemplate, fillTemplate, mailLinkFor, templatesForLead, waLinkFor,
} from "@/lib/crmTemplates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Copy, Mail, MessageCircle } from "lucide-react";

interface Props {
  lead: CrmLead;
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

const CrmMessageComposer = ({ lead, open, onClose, onSent }: Props) => {
  const { toast } = useToast();
  const templates = useMemo(() => templatesForLead(lead), [lead]);
  const [selected, setSelected] = useState<MessageTemplate>(templates[0]);
  const [body, setBody] = useState(fillTemplate(templates[0].body, lead));
  const [subject, setSubject] = useState(fillTemplate(templates[0].subject || "", lead));

  const pick = (t: MessageTemplate) => {
    setSelected(t);
    setBody(fillTemplate(t.body, lead));
    setSubject(fillTemplate(t.subject || "", lead));
  };

  const wa = waLinkFor(lead, body);
  const mail = mailLinkFor(lead, subject, body);

  const register = async (channel: "whatsapp" | "email") => {
    await logEvent(
      lead.id,
      channel === "whatsapp" ? "whatsapp_enviado" : "email_enviado",
      `${channel === "whatsapp" ? "WhatsApp" : "E-mail"} enviado — ${selected.label}`,
      body,
    );
    await updateLead(lead.id, { last_contact_at: new Date().toISOString() });
    onSent();
  };

  const copy = async () => {
    await navigator.clipboard.writeText(body);
    toast({ title: "Mensagem copiada" });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Mensagens — {lead.client_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[11px] font-body uppercase tracking-wide text-muted-foreground">
              Modelos (os da etapa atual aparecem primeiro)
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => pick(t)}
                  className={`px-2.5 py-1.5 rounded-md border font-body text-xs transition-colors ${
                    selected.id === t.id
                      ? "bg-accent text-accent-foreground border-accent font-semibold"
                      : "bg-card text-muted-foreground border-border hover:border-accent/50"
                  }`}
                >
                  {t.label}
                  {t.stages.includes(lead.stage) && (
                    <Badge variant="outline" className="ml-1.5 font-body text-[9px]">
                      etapa atual
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] font-body uppercase tracking-wide text-muted-foreground">
              Assunto (e-mail)
            </Label>
            <Input className="font-body" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] font-body uppercase tracking-wide text-muted-foreground">
              Mensagem (edite livremente antes de enviar)
            </Label>
            <Textarea
              className="font-body min-h-[220px]"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {wa ? (
              <Button asChild variant="gold" onClick={() => void register("whatsapp")}>
                <a href={wa} target="_blank" rel="noreferrer">
                  <MessageCircle className="h-4 w-4" /> Abrir WhatsApp
                </a>
              </Button>
            ) : (
              <Button variant="gold" disabled>
                <MessageCircle className="h-4 w-4" /> Sem WhatsApp cadastrado
              </Button>
            )}
            {mail && (
              <Button asChild variant="outline" onClick={() => void register("email")}>
                <a href={mail}>
                  <Mail className="h-4 w-4" /> Abrir e-mail
                </a>
              </Button>
            )}
            <Button variant="secondary" onClick={copy}>
              <Copy className="h-4 w-4" /> Copiar
            </Button>
          </div>

          <p className="font-body text-[11px] text-muted-foreground">
            O envio é registrado automaticamente na timeline do lead e atualiza a data do último contato.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CrmMessageComposer;
